<script setup lang="ts">
defineOptions({
  name: 'MopFooter',
});

const { $gtm, $mopI18n, $mopConfig } = useNuxtApp();
const { getAlternates } = useMopSeo();
let timer: any = null;

const locales = $mopConfig.getAvailableLocales();

function redirect(newLocale: string) {
  const alternate = getAlternates()?.find((alternate) => alternate.lang === newLocale);
  const win: Window = window;
  win.location = `/${newLocale}${alternate?.href}` || `/${newLocale}`;
}

function handleLocaleSwitch(newLocale: string) {
  if ($mopI18n.locale === newLocale) {
    return;
  }

  timer = setTimeout(() => {
    redirect(newLocale);
  }, 500);
  $gtm.trackEvent(
    {
      eventName: 'language_switch',
      parameters: ['click', $mopI18n.locale, newLocale],
      callbackFn() {
        clearTimeout(timer);
        redirect(newLocale);
      },
    },
    true,
  );
}

function trackEventOnClick(eventName: string, name: string) {
  $gtm.trackEvent({
    eventName,
    parameters: ['click', name],
  });
}
</script>

<template>
  <footer>
    <nav class="footer__nav">
      <UiNuxtLink
        :is="item.getLink().componentName"
        v-for="item in $footerNavigationList"
        :key="item.getUid()"
        v-bind="item.getLink()"
        class="footer__nav-link"
        @click="trackEventOnClick('footer_link', item.getName())"
      >
        {{ item.getName() }}
      </UiNuxtLink>
    </nav>
    <div class="footer__locales">
      <template v-for="(locale, i) in locales" :key="locale">
        <i v-if="i !== 0" class="footer__pipe" />
        <a class="footer__locales-link" :href="`/${locale}`" @click.prevent="handleLocaleSwitch(locale)">
          {{ $mopI18n.t(`locale.lang.${locale}`) }}
        </a>
      </template>
    </div>
    <div class="footer__social">
      <component
        :is="item.getLink().componentName"
        v-for="item in $socialLinkList"
        :key="item.getUid()"
        v-bind="item.getLink()"
        class="footer__social-link"
        @click="trackEventOnClick('social_media_icon', item.getName())"
      >
        {{ item.getName() }}
      </component>
    </div>
    <div class="footer__copyright">
      {{ '&copy;' }} {{ $mopI18n.t('locale.brandName') }} {{ new Date().getFullYear() }}
    </div>
  </footer>
</template>

<style lang="scss" scoped>
footer {
  display: grid;
  grid-template-areas:
    'navigation navigation'
    'locales social'
    'copyright copyright';

  @include apply-upto(medium) {
    grid-template-areas:
      'navigation social'
      'locales locales'
      'copyright copyright';
  }
}

.footer__nav {
  grid-area: navigation;
  padding: $space40 $global-padding;

  @include apply-upto(medium) {
    display: flex;
    flex-direction: column;
    text-align: left;
    justify-content: flex-start;
  }
}

.footer__nav-link {
  @include link-not-underlined(6px);
  @include text-style(strong);

  text-transform: uppercase;
  margin-right: $space50;
  display: inline-block;

  @include apply-upto(medium) {
    margin-right: 0;

    &::after {
      display: none;
    }
  }

  &:last-of-type {
    margin-right: 0;
  }
}

.footer__locales {
  grid-area: locales;
  background: $color-blue;
  padding: 0 $global-padding;
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  @include apply-upto(medium) {
    background: $surface-gray;
    padding-top: $space-12;
  }
}

.footer__locales-link {
  @include link-not-underlined(6px);
  @include text-style(strong);

  text-transform: uppercase;
  color: $white;
}

.footer__pipe {
  display: inline-flex;
  margin: 0 $space10 $space2;
  background: $white;
  height: 13px;
  width: 1px;
}

.footer__social {
  grid-area: social;
  background: $color-blue;
  padding: 0 $space-16;
  text-align: right;

  @include apply-upto(medium) {
    background: inherit;
    display: flex;
    flex-direction: column;
    text-align: left;
    padding: $space40 $global-padding;
  }
}

.footer__social-link {
  @include link-not-underlined(6px);
  @include text-style(strong);

  text-transform: uppercase;
  color: $white;
  margin-left: $space25;
  display: inline-block;

  @include apply-upto(medium) {
    color: inherit;
    margin-left: 0;

    &::after {
      display: none;
    }
  }
}

.footer__copyright {
  @include text-style(strong);

  grid-area: copyright;
  padding: 6px $global-padding;
  background: $surface-gray;
  color: $white;

  @include apply-upto(medium) {
    padding: 12px $global-padding 18px;
  }
}
</style>
